import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { RootState } from '@reducers/index';
import { prefetchProperties } from '@modules/Properties/useProperties';
import { prefetchPropertyHomeData } from '@modules/Properties/home/usePropertyHomeData';
import {
  MARKET_VIEW_KINDS,
  parseView,
} from '@modules/Properties/home/market/marketModel';
import { propertyTabInsights } from '@modules/Properties/home/propertyTabKinds';
import { preloadPropertyMoney } from '@modules/Properties/home/money/LazyPropertyMoney';
import { pickWorkspaceProperty } from './useWorkspaceProperty';

/** Start reads while route code loads. The page still gates rendering on the
 * server's property list; a remembered id is only a speculative, scoped read. */
export function useWorkspacePrefetch() {
  const orgId = useSelector((s: RootState) => s.SelectedOrganization?._id);
  const orgToken = useSelector((s: RootState) => s.SelectedOrganization?.token);
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!orgId) return;
    let live = true;
    let choice: string | null = null;
    let lastKnown: string | null = null;
    try {
      choice = localStorage.getItem(`workspaceProperty:${orgId}`);
      lastKnown = localStorage.getItem(`workspaceLastProperty:${orgId}`);
    } catch {
      /* Private mode. */
    }
    const market = pathname === '/property/market';
    const home = pathname === '/property';
    const creators = pathname === '/creators';
    const params = new URLSearchParams(search);
    const homeInsights = propertyTabInsights(params.get('tab') || 'overview');
    const view = parseView(new URLSearchParams(search).get('view'));
    const warm = (id: string | null | undefined) => {
      if (creators && id) {
        // Keep the CRM code out of the initial bundle and discard late work
        // after leaving the route or switching organization/token.
        void import('./creators/useCreatorsCrm')
          .then(({ prefetchCreatorsCrm }) => {
            if (live) prefetchCreatorsCrm(id);
          })
          .catch(() => undefined);
      }
      if (home && id && homeInsights.kinds.length) {
        const { kinds, ...options } = homeInsights;
        prefetchPropertyHomeData(id, kinds, options);
      }
      if (market && id)
        prefetchPropertyHomeData(id, MARKET_VIEW_KINDS[view], {
          growth: false,
        });
    };
    if (market && view === 'money')
      void preloadPropertyMoney().catch(() => undefined);
    warm(choice || lastKnown);
    void prefetchProperties()
      .then((properties) => {
        if (live) warm(pickWorkspaceProperty(properties, choice)?.id);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [orgId, orgToken, pathname, search]);
}
